import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, ArrowRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS, userFullName } from "@/lib/labels";

const ROLE_VALUES = ["admin", "amministrazione", "back_office", "seller"];

export default function MigrationReview() {
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/admin/migration-review");
      setItems(r.data);
      const e = {};
      r.data.forEach((u) => { e[u.id] = { role_default: u.role_default || "back_office", is_seller: !!u.is_seller }; });
      setEdits(e);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  useEffect(() => { load(); }, []);

  const confirmActivate = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, {
        ...edits[u.id],
        is_active: true,
      });
      toast.success(`${u.email} attivato`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/users" className="text-slate-500 hover:text-slate-700"><ChevronLeft className="h-5 w-5" /></Link>
        <h1 className="font-heading text-2xl font-semibold">Revisione migrazione utenti</h1>
      </div>

      <Card className="p-4 border-amber-300 bg-amber-50 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          Gli utenti elencati erano <code className="px-1 bg-white/60 rounded">USER</code> nello Sprint 0 e sono stati
          temporaneamente disattivati. Assegna il ruolo corretto e attivali singolarmente. I permessi azienda × sede
          potrai assegnarli successivamente da <Link to="/users" className="underline">Utenti</Link>.
        </div>
      </Card>

      {items.length === 0 ? (
        <Card className="p-8 text-center text-slate-400">Nessun utente da revisionare ✅</Card>
      ) : (
        <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 border-b text-left text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-2.5">Nome</th><th className="px-4 py-2.5">Email</th>
                  <th className="px-4 py-2.5">Ruolo</th><th className="px-4 py-2.5">Venditore</th>
                  <th className="px-4 py-2.5">Azione</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-3 font-medium">{userFullName(u)}</td>
                    <td className="px-4 py-3 text-slate-700">{u.email}</td>
                    <td className="px-4 py-3">
                      <Select value={edits[u.id]?.role_default} onValueChange={(v) => setEdits({ ...edits, [u.id]: { ...edits[u.id], role_default: v } })}>
                        <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ROLE_VALUES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Switch checked={!!edits[u.id]?.is_seller} onCheckedChange={(v) => setEdits({ ...edits, [u.id]: { ...edits[u.id], is_seller: v } })} />
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" onClick={() => confirmActivate(u)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`migration-activate-${u.id}`}>
                        Conferma e attiva <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
